import { bpHistory } from 'global/bpHistory';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import GroupModule from 'store/modules/group/groupModule';
import { Agent } from 'types/idTrackerService';
import { getFormInstances, requestSetAnswers } from 'api/operations/formService';
import { FormInstanceType, FormType } from 'types/formService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { getCart } from 'api/operations/cartServiceV2';
import { TinAndGaDelegate } from './tinAndGaDelegate';
import { SelectAgentDelegate } from './selectAgentDelegate';
import { AgentDetailsDelegate } from './agentDetailsDelegate';
import { AlliedRegion, findAlliedRegionByState } from './domain-data/alliedStateRegionMap';

export class AlliedBrokerPageStore {
  private _domainParams: EnrollmentFlowDomainParams;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _existingAgents: Agent[] = [];
  private _formInstance: FormInstanceType | null = null;
  private _group = new GroupModule();
  private _aorRegion: AlliedRegion | null = null;
  private _newAgentInCreation: Agent = {
    agentNumber: '-1',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    npn: '',
    tin: '',
    region: '',
    originalTin: '',
    gaNumber: '',
    gaName: '',
    address: '',
    city: '',
    zip: '',
    state: '',
  };

  private _setFormInstance(formInstance: FormInstanceType): void {
    this._formInstance = formInstance;
  }

  private _setNewAgentInCreation(newAgentInCreation: Agent): void {
    this._newAgentInCreation = newAgentInCreation;
  }

  private async _prepopulateFormAnswers(formAnswers: Record<string, unknown>): Promise<void> {
    if (formAnswers.tin && formAnswers.region && formAnswers.gaNumber) {
      await this.tinAndGaDelegate.populateTinAndGa(formAnswers as unknown as Agent);
    }
    runInAction(() => {
      if (formAnswers.agentNumber) {
        this.selectAgentDelegate.populateAgent(formAnswers as unknown as Agent);
      }
      // Assume if first name is present, the whole agent is too
      if (formAnswers.agentNumber === '-1') {
        this._setNewAgentInCreation(formAnswers as unknown as Agent);
      }
    });
  }

  public tinAndGaDelegate = new TinAndGaDelegate({
    setAvailableAgents: this.onUploadAgents.bind(this),
  });

  public selectAgentDelegate = new SelectAgentDelegate();

  public agentDetailsDelegate = new AgentDetailsDelegate();

  public get availableAgents(): Agent[] {
    return [...this._existingAgents, this._newAgentInCreation];
  }

  public get canCompleteStep(): boolean {
    return (
      this.tinAndGaDelegate.isFormValid && this.selectAgentDelegate.isFormValid && this.agentDetailsDelegate.isFormValid
    );
  }

  public async getInitialData(): Promise<void> {
    this.tinAndGaDelegate.form.setLoading(true);

    // Get forms & enrollment (combined fore better performance)
    const [_group, _enrollment, formInstances] = await Promise.all([
      this._group.getGroup(this._domainParams.groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainParams.periodId),
      getFormInstances(this._domainParams.periodId, FormType.BROKER_FORM),
    ]);

    // Get cart
    await runInAction(async () => {
      if (!this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId) {
        throw new Error('Cannot find a confirmed proposal to get the cart id !');
      }

      const cart = await getCart(this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId);

      // Set form instance & answers
      this._setFormInstance(formInstances[0]);

      // Configure the page based on the info received
      await runInAction(async () => {
        this.tinAndGaDelegate.presetTin(this._group.group?.agentOfRecord?.tin ?? '');

        this._aorRegion = findAlliedRegionByState(cart?.lastUpdateBrowsingCriteria.state ?? '');
        if (!this._aorRegion) {
          throw new Error(`Cannot find the allied region for the state ${cart?.lastUpdateBrowsingCriteria.state} !`);
        }
        this.tinAndGaDelegate.setAorRegion(this._aorRegion);
        const currentBrowsingCriteria = cart.cartItems
          .filter((item) => item.confirmed)
          .map((item) => item.browsingCriteria[item.selectedIndex]);

        await this.tinAndGaDelegate.loadAgencies(currentBrowsingCriteria);
        await this._prepopulateFormAnswers(formInstances[0].answers.common);
        runInAction(() => {
          this.tinAndGaDelegate.form.setLoading(false);
        });
      });
    });
  }

  public setExistingAgents(agents: Agent[]): void {
    this._existingAgents = agents;
  }

  public onUploadAgents(agents: Agent[]): void {
    this.selectAgentDelegate.setAgents(agents);
    this.setExistingAgents(agents);
  }

  public populateSelectedAgent(selectedAgentId: string | undefined): void {
    if (selectedAgentId) {
      const agent = this.availableAgents.find((currentAgent: Agent) => currentAgent.agentNumber === selectedAgentId);
      if (agent) {
        const agentOverrides =
          this._formInstance?.answers.common.agentNumber === agent.agentNumber
            ? { ...agent, ...this._formInstance?.answers.common }
            : agent;
        this.agentDetailsDelegate.populateAgentDetails(agentOverrides);
      } else {
        this.agentDetailsDelegate.form.resetForm();
      }
      this.agentDetailsDelegate.setFormIsHidden(false);
    } else {
      this.agentDetailsDelegate.setFormIsHidden(true);
    }
  }

  public resetPage(): void {
    this.selectAgentDelegate.form.resetForm();
    this.agentDetailsDelegate.form.resetForm();
    this.agentDetailsDelegate.setFormIsHidden(true);
    this.setExistingAgents([]);
  }

  public onBackRedirect(): void {
    bpHistory.push(`/checklist/${this._domainParams.periodId}`);
  }

  public async onComplete(): Promise<void> {
    this.agentDetailsDelegate.form.setLoading(true);

    const agentData = {
      tin: this.tinAndGaDelegate.form.fields.tin.value,
      region: this._aorRegion,
      gaNumber: this.tinAndGaDelegate.form.fields.ga.value,
      gaName: this.tinAndGaDelegate.gaName,
      agentNumber: this.selectAgentDelegate.selectedAgentNumber,
      ...this.agentDetailsDelegate.form.getFormDataObject(),
    };
    await requestSetAnswers({
      formInstanceId: this._formInstance?.id ?? '',
      answers: {
        common: agentData,
        custom: {},
      },
      complete: true,
    });
    toastManager.addToast('Agent data saved successfully!', ToastColorTypes.SUCCESS);
    await runInAction(async () => {
      await requestApplyUserAction(this._domainParams.periodId, USER_ACTIONS.COMPLETE_BROKER);
      runInAction(() => {
        this.agentDetailsDelegate.form.setLoading(false);
        bpHistory.push(`/checklist/${this._domainParams.periodId}`);
      });
    });
  }

  constructor(domainParams: EnrollmentFlowDomainParams) {
    this._domainParams = domainParams;
    makeAutoObservable(this, {}, { autoBind: true });

    autorun(() => {
      const isTinFormValid = this.tinAndGaDelegate.isFormValid;
      runInAction(() => {
        this.selectAgentDelegate.setFormIsBlocked(!isTinFormValid);
      });
    });

    autorun(() => {
      const availableAgents = this.availableAgents;
      runInAction(() => {
        this.selectAgentDelegate.setAgents(availableAgents);
      });
    });

    autorun(() => {
      const selectedAgentId = this.selectAgentDelegate.selectedAgentId;
      runInAction(() => {
        if (selectedAgentId) {
          this.populateSelectedAgent(selectedAgentId);
        }
      });
    });
  }
}

export const AlliedBrokerPageStoreContext = createContext<AlliedBrokerPageStore>(
  new AlliedBrokerPageStore({} as EnrollmentFlowDomainParams),
);

export const useAlliedBrokerPageStore = (): AlliedBrokerPageStore => {
  return useContext(AlliedBrokerPageStoreContext);
};
